import { capitalizeFirstLetter } from '@autone/utils';
import { Checkbox, MenuItem, Stack, Typography } from '@mui/material';
import type { HTMLAttributes, ReactElement } from 'react';

import { DisabledReasonIcon } from './DisabledReasonIcon';
import { type MultiSelectOption } from './MultiSelect.types';

type FormOption<P> = {
  option: P;
  optionProps: HTMLAttributes<HTMLLIElement>;
  selected: boolean;
  showId?: boolean;
  itemSlot?: ({
    option,
    disabled,
    disabledReason,
  }: {
    option: P;
    disabled?: boolean;
    disabledReason?: string;
  }) => ReactElement;
  disabled?: boolean;
  disabledReason?: string;
};

const MultiSelectFormOption = <P extends MultiSelectOption>({
  option,
  optionProps,
  selected,
  showId,
  itemSlot,
  disabled,
  disabledReason,
}: FormOption<P>) => {
  const { id, description } = option;
  const sharedSx = {
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    maxWidth: '100%',
  };

  if (itemSlot)
    return (
      <MenuItem
        key={`${id}-${description}`}
        selected={selected}
        {...optionProps}
        className={'py-0'}
        disabled={disabled}
      >
        {itemSlot({
          option,
          disabled,
          disabledReason,
        })}
      </MenuItem>
    );

  return (
    <MenuItem
      key={`${id}-${description}`}
      selected={selected}
      disabled={disabled}
      {...optionProps}
    >
      <Checkbox sx={{ p: 0, mr: 1 }} checked={selected} />
      <Stack sx={{ width: '90%' }}>
        <Typography noWrap sx={sharedSx}>
          {capitalizeFirstLetter(description)}
        </Typography>
        {showId && (
          <Typography color="text.secondary" noWrap sx={sharedSx}>
            {id}
          </Typography>
        )}
      </Stack>
      {disabled && disabledReason && (
        <DisabledReasonIcon disabledReason={disabledReason} />
      )}
    </MenuItem>
  );
};

export default MultiSelectFormOption;
